import { useState } from 'react';

interface Props {
  value: string;        // serialized as comma-separated for compatibility
  onChange: (v: string) => void;
  placeholder?: string;
  testId?: string;
}

/**
 * Tag-chip input: user types tokens separated by Enter or comma; tokens
 * render as removable pills. The serialized string value remains
 * comma-separated so it round-trips with the existing in/not_in semantics.
 */
export default function ChipInput({ value, onChange, placeholder, testId }: Props) {
  const tokens = value
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean);
  const [draft, setDraft] = useState('');

  function commit(t: string) {
    const next = [...tokens, t.trim()].filter(Boolean);
    onChange(next.join(', '));
  }

  function remove(idx: number) {
    const next = tokens.filter((_, i) => i !== idx);
    onChange(next.join(', '));
  }

  return (
    <div
      className="ff-input flex flex-wrap items-center gap-1.5 py-2 px-2 min-h-[40px]"
      data-testid={testId}
    >
      {tokens.map((t, idx) => (
        <span
          key={idx}
          className="inline-flex items-center gap-1 bg-ff-mint text-ff-forest text-xs font-mono px-2 py-0.5 rounded-md"
        >
          {t}
          <button
            type="button"
            onClick={() => remove(idx)}
            aria-label={`Remove ${t}`}
            className="text-ff-forest/60 hover:text-ff-danger text-[14px] leading-none"
          >
            ×
          </button>
        </span>
      ))}
      <input
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ',') && draft.trim()) {
            e.preventDefault();
            commit(draft);
            setDraft('');
          } else if (e.key === 'Backspace' && draft === '' && tokens.length > 0) {
            remove(tokens.length - 1);
          }
        }}
        onBlur={() => {
          if (draft.trim()) {
            commit(draft);
            setDraft('');
          }
        }}
        placeholder={tokens.length === 0 ? (placeholder ?? 'value, value, value') : ''}
        className="flex-1 min-w-[80px] outline-none bg-transparent text-sm font-mono"
      />
    </div>
  );
}
